import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import Main from 'components/app/Main';
import SEO from 'components/shared/SEO';
import Heading from 'components/shared/Heading';
import ControlledTabs from 'components/shared/ControlledTabs';
import Text from 'components/shared/Text';
import Table from 'components/shared/Table';
import MarketId from 'components/shared/MarketId';
import SearchInput from 'components/shared/SearchInput';
import StickyContainer from 'components/shared/StickyContainer';
import { useIsMobile, useAssets } from 'helpers/hooks';
import { formatAmount } from '@nbx/frontend-helpers/format';
import { isNullish } from 'helpers/objects';
import { isNaNish } from 'helpers/balances';
import { useMarkets } from 'helpers/hooks';
import { useTickers as useTickersApi } from '@nbx/providers';
import styles from './styles.module.scss';
import { useLocalCurrencyTab } from './useLocalCurrencyTab';

const microapps = window.MICROFRONTENDS_CONFIG;

const columnWidths = [1.833, 1, 1.833, 1.833, 1.833, 1.833, 1.833];
const columnWidthsMobile = [6, 6];
const endAlignIndex = 1;
const TABS_ASSETS = ['NOK', 'SEK', 'DKK', 'EUR', 'USDM'];

function Markets() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const assets = useAssets();
  const { data: marketsData } = useMarkets();

  const {
    data: tickersApiData,
    isLoading: isTickersLoading,
    isError: isTickersError,
    refetch: refetchTickers,
    error: tickersError
  } = useTickersApi();

  const headers = [
    t('common_market'),
    t('common_coin'),
    t('market_last_price'),
    t('market_24h_change'),
    t('market_24h_high'),
    t('market_24h_low'),
    t('market_24h_volume')
  ];
  const headersMobile = [t('common_assets'), t('market_price_24h_change')];

  useEffect(() => {
    if (isMobile) setQuery('');
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && window.scrollY > 60) window.scroll({ top: 60 }); // eslint-disable-line
  }, [isMobile, activeTabIndex]);

  const tickersDataSorted = useMemo(
    () =>
      tickersApiData
        ?.map(ticker => ({
          ...ticker,
          volumeDecimalsPrecisionDisplay: assets.data[ticker.baseAsset]?.decimalPrecisionDisplay,
          name: `${ticker.baseAsset}/${ticker.quoteAsset}`,
          baseCurrency: ticker.baseAsset,
          quoteCurrency: ticker.quoteAsset,
          minTickLength: marketsData?.markets?.find(({ id }) => id === ticker.id)?.minTickLength ?? 2,
          change: `${ticker.change.toFixed(2)}%`
        }))
        .sort((t1, t2) => t2.volumeQuote - t1.volumeQuote),
    [tickersApiData, assets.data, marketsData]
  );

  const getChangeFigure = ticker => {
    let { isChangeUp } = ticker;
    let changeUpSymbol = isChangeUp ? '+' : '-';
    let changeFloat = Math.abs(parseFloat(ticker?.change));
    const noData = isNullish(ticker.change) || isNaNish(changeFloat) || changeFloat === 0;
    if (noData) {
      changeFloat = 0;
      isChangeUp = null;
      changeUpSymbol = '';
    }
    return (
      <Text
        styles={[
          styles.changeFigure,
          {
            [styles.changeUp]: !noData && isChangeUp,
            [styles.changeDown]: !noData && !isChangeUp
          }
        ]}
        monospaced
        muted={noData}>
        {changeUpSymbol}
        {changeFloat.toFixed(2)}%
      </Text>
    );
  };

  const renderItem = useCallback(
    (ticker, index) =>
      ({
        0: <MarketId pair={ticker.id} />,
        1: <Text muted>{ticker.baseCurrency}</Text>,
        2: (
          <Text monospaced>
            {!isNaNish(ticker.price) ? formatAmount(ticker.price, ticker.minTickLength) : '-'}
          </Text>
        ),
        3: getChangeFigure(ticker),
        4: (
          <Text monospaced>
            {!isNaNish(ticker.high) ? formatAmount(ticker.high, ticker.minTickLength) : '-'}
          </Text>
        ),
        5: (
          <Text monospaced>
            {!isNaNish(ticker.low) ? formatAmount(ticker.low, ticker.minTickLength) : '-'}
          </Text>
        ),
        6: (
          <Text monospaced>
            {!isNaNish(ticker.volume)
              ? formatAmount(ticker.volume, ticker.volumeDecimalsPrecisionDisplay)
              : '-'}
          </Text>
        )
      }[index]),
    []
  );

  const renderItemMobile = useCallback(
    (ticker, index) =>
      ({
        0: <MarketId pair={ticker.id} iconSize="18" />,
        1: (
          <span className={styles.price}>
            <Text weight="medium" monospaced styles={[styles.priceText]}>
              {!isNaNish(ticker.price) ? formatAmount(ticker.price, ticker.minTickLength) : '-'}
            </Text>
            {getChangeFigure(ticker)}
          </span>
        )
      }[index]),
    []
  );

  const renderLink = useCallback(
    ticker => `${microapps.routes.trading.path.split(':')[0]}${ticker.pair}`,
    []
  );

  const filterQuery = useCallback(
    market => {
      if (query === '') return true;
      const lcQuery = query.toLowerCase();
      return market.name.toLowerCase().includes(lcQuery);
    },
    [query]
  );

  const filterData = useCallback(data => data?.filter(filterQuery), [filterQuery]);
  const refetchOnError = () => {
    if (assets.error) assets.refetch();
    if (isTickersError) refetchTickers();
  };
  const loading = isTickersLoading || assets.loading;
  const renderTable = data => (
    <>
      {!isMobile && (
        <div className={styles.headerFilterWrapper}>
          {!isTickersLoading && !isTickersError && (
            <SearchInput
              id="marketFilter"
              name={t('market_filter_markets')}
              noLabel
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          )}
        </div>
      )}
      <Table
        data={filterData(data) || []}
        keyField="id"
        isLoading={loading}
        error={tickersError}
        refetchData={refetchOnError}
        endAlignIndex={endAlignIndex}
        headers={isMobile ? headersMobile : headers}
        renderItem={isMobile ? renderItemMobile : renderItem}
        renderLink={renderLink}
        emptyTitle={t('market_no_markets_match_query')}
        columnWidths={isMobile ? columnWidthsMobile : columnWidths}
        rowHeight={isMobile ? 40 : 66}
        borders={!isMobile}
      />
      <nbx-text legal slot="label">
        <Trans i18nKey="tradingview_attribution">
          <nbx-button-text
            legal
            href="https://www.tradingview.com/markets/cryptocurrencies/prices-all/"
            underlined
          />
        </Trans>
      </nbx-text>
    </>
  );

  const tabs = useMemo(
    () => [
      { label: t('common_all'), isFilter: false },
      ...TABS_ASSETS.map(label => ({ label, isFilter: true }))
    ],
    [t]
  );

  useLocalCurrencyTab({ tabs, setActiveTabIndex });

  const tickersDataFiltered = useMemo(() => {
    const activeTab = tabs[activeTabIndex];
    return activeTab.isFilter
      ? tickersDataSorted?.filter(ticker => ticker.pair.includes(activeTab.label))
      : tickersDataSorted;
  }, [tabs, activeTabIndex, tickersDataSorted]);

  return (
    <Main>
      <SEO
        title={t('market_meta_title')}
        description={t('market_meta_descr')}
        keywords={t('market_meta_keywords')}
      />
      <div className={classnames([{ [styles.markets]: !isMobile }])}>
        {isMobile ? (
          <StickyContainer bg>
            <Heading tag="h3" styles={[styles.heading]}>
              {t('header_link_markets')}
            </Heading>
            <ControlledTabs
              tabs={tabs}
              activeTabIndex={activeTabIndex}
              setActiveTabIndex={setActiveTabIndex}
            />
          </StickyContainer>
        ) : (
          <ControlledTabs tabs={tabs} activeTabIndex={activeTabIndex} setActiveTabIndex={setActiveTabIndex} />
        )}
        {renderTable(tickersDataFiltered)}
      </div>
    </Main>
  );
}

export default Markets;
